'use client'

import classNames from 'classnames'
import { FC, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Category } from '@/app/types/blog'

interface Props {
	categories: Category[]
	activeCategory?: string | null
	baseBlogPath: string
	allLabel: string
	onCategorySelect?: (categorySlug: string) => void
	resetScrollOnMount?: boolean
}

const CategoryChips: FC<Props> = ({
	categories,
	activeCategory = null,
	baseBlogPath,
	allLabel,
	onCategorySelect,
	resetScrollOnMount = false,
}) => {
	const isAllActive = activeCategory === 'all'
	const containerRef = useRef<HTMLDivElement | null>(null)
	const [showLeftFade, setShowLeftFade] = useState(false)

	const handleScroll = () => {
		if (containerRef.current) {
			const scrollLeft = containerRef.current.scrollLeft
			const shouldShow = scrollLeft > 40
			setShowLeftFade(shouldShow)
		}
	}

	useEffect(() => {
		if (resetScrollOnMount && containerRef.current) {
			containerRef.current.scrollLeft = 0
		}
	}, [resetScrollOnMount])

	useEffect(() => {
		// Check initial scroll state
		handleScroll()
	}, [])

	const handleClick = (categorySlug: string) => {
		if (onCategorySelect) {
			onCategorySelect(categorySlug)
		}
	}

	return (
		<div className="category-chips-wrapper">
			<div
				className="category-chips"
				ref={containerRef}
				onScroll={handleScroll}
			>
				{/* "All" chip - always first */}
				{onCategorySelect ? (
					<button
						type="button"
						onClick={() => handleClick('all')}
						className={classNames(
							'category-chips__chip category-chips__chip--all',
							{
								'category-chips__chip--active': isAllActive,
							}
						)}
						aria-pressed={isAllActive}
					>
						{allLabel}
					</button>
				) : (
					<Link
						href={`${baseBlogPath}/topics/all`}
						className={classNames(
							'category-chips__chip category-chips__chip--all',
							{
								'category-chips__chip--active': isAllActive,
							}
						)}
					>
						{allLabel}
					</Link>
				)}

				{/* All categories in their original order */}
				{categories.map((category) => {
					const isActive = category.slug.current === activeCategory
					const href = `${baseBlogPath}/topics/${category.slug.current}`
					return onCategorySelect ? (
						<button
							type="button"
							key={category._id}
							onClick={() => handleClick(category.slug.current)}
							className={classNames('category-chips__chip', {
								'category-chips__chip--active': isActive,
							})}
							aria-pressed={isActive}
						>
							{category.title}
						</button>
					) : (
						<Link
							key={category._id}
							href={href}
							className={classNames('category-chips__chip', {
								'category-chips__chip--active': isActive,
							})}
						>
							{category.title}
						</Link>
					)
				})}
			</div>
			<div
				className={classNames('category-chips__fade category-chips__fade--left', {
					'category-chips__fade--left--visible': showLeftFade,
				})}
				aria-hidden
			/>
			<div className="category-chips__fade" aria-hidden />
		</div>
	)
}

export default CategoryChips
